import { Layers, FolderOpen } from 'lucide-react';
import type { FlashcardDeck, FlashcardStudyScope } from '../../services/flashcardService';
import type { Tutee } from '../../types/tuition';

interface FlashcardDeckPickerProps {
  tutee: Tutee;
  decks: FlashcardDeck[];
  studyScope: FlashcardStudyScope;
  onStudyScopeChange: (scope: FlashcardStudyScope) => void;
  showUnassigned?: boolean;
}

export function studyScopeLabel(scope: FlashcardStudyScope, decks: FlashcardDeck[]): string {
  if (scope === 'all') return 'All decks';
  if (scope === 'unassigned') return 'Unassigned';
  return decks.find((d) => d.id === scope)?.name ?? 'Selected deck';
}

const FlashcardDeckPicker = ({
  tutee,
  decks,
  studyScope,
  onStudyScopeChange,
  showUnassigned = false,
}: FlashcardDeckPickerProps) => {
  if (decks.length === 0 && !showUnassigned) return null;

  const activeClass = `bg-gradient-to-r ${tutee.colorScheme.gradient} text-white shadow-md`;
  const inactiveClass = 'bg-white/80 text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-white';

  const pillClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold transition-all active:scale-[0.98] ${
      active ? activeClass : inactiveClass
    }`;

  return (
    <div className="mb-4 p-4 bg-white/70 backdrop-blur rounded-2xl border border-white/60 shadow-sm">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <FolderOpen className="w-3.5 h-3.5" />
        Study from
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onStudyScopeChange('all')}
          className={pillClass(studyScope === 'all')}
        >
          <Layers className="w-4 h-4" />
          All decks
        </button>
        {decks.map((deck) => (
          <button
            key={deck.id}
            type="button"
            onClick={() => onStudyScopeChange(deck.id)}
            className={pillClass(studyScope === deck.id)}
          >
            {deck.name}
          </button>
        ))}
        {showUnassigned && (
          <button
            type="button"
            onClick={() => onStudyScopeChange('unassigned')}
            className={pillClass(studyScope === 'unassigned')}
          >
            Unassigned
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        {studyScope === 'all'
          ? 'Cards from every deck are mixed in one study session.'
          : `Only cards from “${studyScopeLabel(studyScope, decks)}” will appear when you study.`}
      </p>
    </div>
  );
};

export default FlashcardDeckPicker;
